import React, { useState, useRef, useEffect } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { FiCheck, FiMapPin, FiArrowLeft, FiLink, FiPlus, FiX } from 'react-icons/fi';
import CustomSelectOption from '../../../base_component/CustomSelectOption/CustomSelectOption';
import { HiUser } from "react-icons/hi2";
import { X } from 'lucide-react';
import { FaInstagram, FaFacebook, FaSnapchat } from 'react-icons/fa';
import { FaXTwitter } from "react-icons/fa6";
import CustomSelect from '../../../base_component/CustomSelectStatus/CustomSelectStatus';
import EllipsisLoader from '../../../base_component/EllipsisLoader/EllipsisLoader';
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import Autocomplete from 'react-google-autocomplete';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getMediumsAPI, getStatusAPI, accountSetupAPI } from '../../../service/ApiServices';
import ProfileImageCrop from '../../../components/ProfileImageCrop';

const GOOGLE_MAPS_API_KEY = "AIzaSyC9G8OXUWRk3F5Bv6WaH6atdW55_6wHmZ8";
const GOOGLE_MAPS_LIBRARIES = ['places'];

const profileValidationSchema = Yup.object({
    artTypes: Yup.array()
        .required('Art type is required'),
    bio: Yup.string()
        .required('Short Bio is required')
        .min(10, 'Bio must be at least 10 characters')
        .max(500, 'Short Bio must be 500 characters or less'),
}).strict();

const studioValidationSchema = Yup.object({
    studioName: Yup.string()
        .required('Studio Name is required')
        .min(2, 'Studio Name must be at least 2 characters')
        .max(100, 'Studio Name must be 100 characters or less'),
    location: Yup.string()
        .required('Location is required'),
    bannerNumber: Yup.string()
        .required('Buzzer Number is required')
        .min(2, 'Buzzer Number must be at least 2 character')
        .max(50, 'Buzzer Number must be 50 characters or less'),
    status: Yup.string().required("Please select a status")
}).strict();

const socialsValidationSchema = Yup.object().shape({
    artistStatement: Yup.string()
        .required('Artist statement is required')
        .min(10, 'Artist statement must be at least 10 characters')
        .max(1000, 'Artist statement must be 1000 characters or less'),
    socialLinks: Yup.array().of(
        Yup.object().shape({
            url: Yup.string().url('Must be a valid URL').nullable()
        })
    )
});

const LocationModal = ({ isOpen, onClose, onLocationSelect, initialAddress, canterlocatlon }) => {
    const mapRef = useRef(null);
    const formikRef = useRef(null);
    const [selectedLocation, setSelectedLocation] = useState(null);

    const addressValidationSchema = Yup.object({
        streetAddress: Yup.string()
            .required('Street Address is required')
            .min(2, 'Street Address must be at least 2 characters'),
        city: Yup.string()
            .required('City is required')
            .min(2, 'City must be at least 2 characters'),
        state: Yup.string()
            .required('State is required')
            .min(2, 'State must be at least 2 characters'),
        zipCode: Yup.string()
            .required('Zip Code is required'),
        aptSuite: Yup.string().nullable()
    });

    const initialAddressValues = {
        streetAddress: '',
        aptSuite: '',
        city: '',
        state: '',
        zipCode: ''
    };

    useEffect(() => {
        if (initialAddress && formikRef.current) {
            formikRef.current.setFieldValue('streetAddress', initialAddress.streetAddress || '');
            formikRef.current.setFieldValue('aptSuite', initialAddress.aptSuite || '');
            formikRef.current.setFieldValue('city', initialAddress.city || '');
            formikRef.current.setFieldValue('state', initialAddress.state || '');
            formikRef.current.setFieldValue('zipCode', initialAddress.zipCode || '');

            if (initialAddress.lat && initialAddress.lng) {
                setSelectedLocation({
                    lat: initialAddress.lat,
                    lng: initialAddress.lng
                });
            }
        }
    }, [initialAddress]);

    useEffect(() => {
        if (isOpen && initialAddress && formikRef.current) {
            setTimeout(() => {
                if (formikRef.current) {
                    formikRef.current.setFieldValue('streetAddress', initialAddress.streetAddress || '');
                    formikRef.current.setFieldValue('aptSuite', initialAddress.aptSuite || '');
                    formikRef.current.setFieldValue('city', initialAddress.city || '');
                    formikRef.current.setFieldValue('state', initialAddress.state || '');
                    formikRef.current.setFieldValue('zipCode', initialAddress.zipCode || '');

                    if (initialAddress.lat && initialAddress.lng) {
                        setSelectedLocation({
                            lat: initialAddress.lat,
                            lng: initialAddress.lng
                        });
                    }
                }
            }, 100);
        }
    }, [isOpen, initialAddress]);

    const mapContainerStyle = {
        width: '100%',
        height: '100%',
        borderRadius: '0.5rem'
    };

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES
    });

    const handlePlaceSelect = (place) => {
        if (!place.geometry) {
            return;
        }

        const { lat, lng } = place.geometry.location;
        setSelectedLocation({ lat: lat(), lng: lng() });        

        let streetNumber = '';
        let streetName = '';
        let streetAddress = place.formatted_address;
        let aptSuite = '';
        let city = '';
        let state = '';
        let zipCode = '';

        if (place.address_components && place.address_components.length > 0) {
            place.address_components.forEach(component => {
                const types = component.types;

                if (types.includes('street_number')) {
                    streetNumber = component.long_name;
                }
                if (types.includes('route') || types.includes('landmark')) {
                    streetName = component.long_name;
                }
                if (types.includes('premise')) {
                    aptSuite = component.long_name;
                }
                if (types.includes('administrative_area_level_3') || types.includes('locality')) {
                    city = component.long_name;
                }
                if (types.includes('administrative_area_level_1')) {
                    state = component.short_name;
                }
                if (types.includes('postal_code')) {
                    zipCode = component.long_name;
                }
            });
        } else {
            const formattedAddress = place.formatted_address || '';

            if (formikRef.current) {
                formikRef.current.setFieldValue('streetAddress', formattedAddress);
                formikRef.current.setFieldValue('aptSuite', '');
                formikRef.current.setFieldValue('city', '');
                formikRef.current.setFieldValue('state', '');
                formikRef.current.setFieldValue('zipCode', '');
            }
        }

        if (formikRef.current) {
            formikRef.current.setFieldValue('streetAddress', `${streetName} ${streetAddress}`.trim());
            formikRef.current.setFieldValue('aptSuite', `${streetNumber} ${aptSuite}`.trim());
            formikRef.current.setFieldValue('city', city);
            formikRef.current.setFieldValue('state', state);
            formikRef.current.setFieldValue('zipCode', zipCode);
        }

        if (formikRef.current) {
            formikRef.current.setTouched({});
        }

        if (mapRef.current) {
            mapRef.current.panTo({ lat: lat(), lng: lng() });
        }
    };

    const handleMapClick = (event) => {
        setSelectedLocation({
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
        });

        if (window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: event.latLng }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const addressComponents = results[0].address_components;

                    let streetNumber = '';
                    let streetName = '';
                    let streetAddress = results[0].formatted_address;
                    let aptSuite = '';
                    let city = '';
                    let state = '';
                    let zipCode = '';

                    addressComponents.forEach(component => {

                        const types = component.types;
                        if (types.includes('street_number')) {
                            streetNumber = component.long_name;
                        }
                        if (types.includes('route') || types.includes('landmark')) {
                            streetName = component.long_name;
                        }
                        if (types.includes('premise')) {
                            aptSuite = component.long_name;
                        }
                        if (types.includes('administrative_area_level_3') || types.includes('locality')) {
                            city = component.long_name;
                        }
                        if (types.includes('administrative_area_level_1')) {
                            state = component.short_name;
                        }
                        if (types.includes('postal_code')) {
                            zipCode = component.long_name;
                        }
                    });

                    if (formikRef.current) {
                        formikRef.current.setFieldValue('streetAddress', `${streetName} ${streetAddress}`.trim());
                        formikRef.current.setFieldValue('aptSuite', `${streetNumber} ${aptSuite}`.trim());
                        formikRef.current.setFieldValue('city', city);
                        formikRef.current.setFieldValue('state', state);
                        formikRef.current.setFieldValue('zipCode', zipCode);
                    }

                    if (formikRef.current) {
                        formikRef.current.setTouched({});
                    }
                }
            });
        }
    };

    const handleMapLoad = (map) => {
        mapRef.current = map;
    };


    const handleSave = (values) => {
        if (!selectedLocation) {
            toast.error('Please select a location on the map or search for an address');
            return;
        }

        const addressParts = [];

        const aptSuiteNum = values.aptSuite ? values.aptSuite.trim() : '';
        const streetAddr = values.streetAddress ? values.streetAddress.trim() : '';
        
        if (aptSuiteNum && !streetAddr.includes(aptSuiteNum)) {
            addressParts.push(aptSuiteNum);
        }

        if (streetAddr) {
            addressParts.push(streetAddr);
        }

        const fullAddress = addressParts.join(', ');

        const updatedSelectedLocation = {
            lat: selectedLocation.lat,
            lng: selectedLocation.lng,
            streetAddress: values.streetAddress || '',
            aptSuite: values.aptSuite || '',
        };

        canterlocatlon({
            lat: selectedLocation.lat,
            lng: selectedLocation.lng,
            ...updatedSelectedLocation
        });

        onLocationSelect(fullAddress);
    };

    useEffect(() => {
        if (isOpen && mapRef.current && isLoaded) {
            setTimeout(() => {
                const map = mapRef.current;
                if (map && window.google) {
                    window.google.maps.event.trigger(map, 'resize');
                    if (selectedLocation) {
                        map.setCenter(selectedLocation);
                    }
                }
            }, 300);
        }
    }, [isOpen, selectedLocation, isLoaded]);

    if (!isOpen) return null;
    if (loadError) return <div>Error loading map</div>;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-99 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white w-full max-w-5xl rounded-lg overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-fit h-[80vh] overflow-y-auto">
                    <div className="col-span-12 lg:col-span-6 relative">
                        <div className="relative lg:h-full h-[30vh]">
                            {!isLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg z-10">
                                    <div className="text-center">
                                        <div className="animate-spin h-8 w-8 border-2 border-b-0 rounded-full mx-auto mb-2 border-gray-800"></div>
                                        <div>Loading map...</div>
                                    </div>
                                </div>
                            )}
                            {isLoaded && (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={selectedLocation || { lat: 40.7580, lng: -73.9855 }}
                                    zoom={15}
                                    onClick={handleMapClick}
                                    onLoad={handleMapLoad}
                                    options={{
                                        zoomControl: true,
                                        streetViewControl: false,
                                        mapTypeControl: false,
                                        fullscreenControl: false,
                                    }}
                                >
                                    {selectedLocation && <Marker position={selectedLocation} />}
                                </GoogleMap>
                            )}
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-6 p-5 rounded-lg space-y-4">
                        <div className="flex justify-between items-center">
                            <h2 className="md:text-lg text-base font-semibold">Studio Location</h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                                <FiX size={20} />
                            </button>
                        </div>

                        <Formik
                            innerRef={formikRef}
                            key={JSON.stringify(initialAddress)}
                            initialValues={initialAddress || initialAddressValues}
                            validationSchema={addressValidationSchema}
                            onSubmit={handleSave}
                            enableReinitialize={true}
                        >
                            {({ values, errors, touched, handleChange, handleBlur, isValid, dirty }) => (
                                <Form className="space-y-4 text-sm">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Street Address *</label>
                                        <Autocomplete
                                            onPlaceSelected={handlePlaceSelect}
                                            options={{
                                                types: ['geocode', 'establishment'],
                                                fields: ['address_components', 'geometry', 'formatted_address']
                                            }}
                                            placeholder="Enter street address"
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${touched.streetAddress && errors.streetAddress
                                                ? 'border-red-500'
                                                : 'border-gray-300 focus:border-gray-400'
                                                }`}
                                            apiKey={GOOGLE_MAPS_API_KEY}
                                            value={values.streetAddress}
                                            onChange={(e) => handleChange({ target: { name: 'streetAddress', value: e.target.value } })}
                                        />
                                        {touched.streetAddress && errors.streetAddress && (
                                            <div className="text-red-500 text-xs mt-1">{errors.streetAddress}</div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Apt, Suite, etc.</label>
                                        <input
                                            type="text"
                                            name="aptSuite"
                                            placeholder="Enter apartment or suite number (optional)"
                                            value={values.aptSuite}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                                        />
                                    </div>

                                    <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">City *</label>
                                            <input
                                                type="text"
                                                name="city"
                                                placeholder="Enter city"
                                                value={values.city}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${touched.city && errors.city
                                                    ? 'border-red-500'
                                                    : 'border-gray-300 focus:border-gray-400'
                                                    }`}
                                            />
                                            {touched.city && errors.city && (
                                                <div className="text-red-500 text-xs mt-1">{errors.city}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">State *</label>
                                            <input
                                                type="text"
                                                name="state"
                                                placeholder="Enter state"
                                                value={values.state}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${touched.state && errors.state
                                                    ? 'border-red-500'
                                                    : 'border-gray-300 focus:border-gray-400'
                                                    }`}
                                            />
                                            {touched.state && errors.state && (
                                                <div className="text-red-500 text-xs mt-1">{errors.state}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Zip Code *</label>
                                        <input
                                            type="text"
                                            name="zipCode"
                                            placeholder="Enter zip code"
                                            value={values.zipCode}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${touched.zipCode && errors.zipCode
                                                ? 'border-red-500'
                                                : 'border-gray-300 focus:border-gray-400'
                                                }`}
                                        />
                                        {touched.zipCode && errors.zipCode && (
                                            <div className="text-red-500 text-xs mt-1">{errors.zipCode}</div>
                                        )}
                                    </div>

                                    <div className='pt-3'>
                                        <button
                                            type="submit"
                                            className={`w-full py-3 px-4 rounded-lg bg-black text-white hover:bg-gray-800`}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function AccountSetup() {
    const navigate = useNavigate()
    const [currentStep, setCurrentStep] = useState(1);
    const [profileImage, setProfileImage] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [tempImageFile, setTempImageFile] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const setFieldValueRef = useRef(null);
    const [initialLocationStructuredAddress, setInitialLocationStructuredAddress] = useState(null);
    const [mediums, setMediums] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [mapCenter, setMapCenter] = useState({
        lat: 40.7580,
        lng: -73.9855,
    });
    const [parsedAddressForModal, setParsedAddressForModal] = useState(null);
    const user_id = localStorage.getItem('hoast_user_id');

    const profileInitialValues = {
        artTypes: [],
        bio: '',
    };

    const [step1Data, setStep1Data] = useState(profileInitialValues);

    const studioInitialValues = {
        studioName: '',
        location: '',
        bannerNumber: '',
        status: ""
    };

    const [step2Data, setStep2Data] = useState(studioInitialValues);

    const socialsInitialValues = {
        artistStatement: '',
        socialLinks: [
            { platform: 'instagram', url: '', icon: 'FaInstagram' },
            { platform: 'facebook', url: '', icon: 'FaFacebook' },
            { platform: 'X', url: '', icon: 'FaXTwitter' },
            { platform: 'snapchat', url: '', icon: 'FaSnapchat' }
        ]
    };

    const containerStyle = {
        height: "120px",
        width: "100%",
        borderRadius: "14px",
    };

    const { isLoaded: isMainMapLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES
    });

    const getMediums = () => {
        getMediumsAPI()
            .then((res) => {
                const list = res?.data?.data?.mediums || [];
                const formatted = list.map((item) => ({
                    label: (item?.name || "").trim(),
                    value: item?.id,
                }));
                setMediums(formatted);
            })
            .catch((err) => {
                let message = err?.response?.data?.message;

                if (typeof message === 'object' && message !== null) {
                    if (message.error && message.error.errors && Array.isArray(message.error.errors)) {
                        const validationErrors = message.error.errors.map(err => err.msg).join(', ');
                        message = `Validation errors: ${validationErrors}`;
                    } else if (message.message) {
                        message = message.message;
                    } else if (message.error) {
                        message = message.error;
                    } else {
                        message = JSON.stringify(message);
                    }
                }

                if (err?.response?.status === 401) {
                    localStorage.removeItem('hoast_token')
                    localStorage.removeItem('hoast_refresh_token')
                    navigate('/login');
                }
                const errorMessage = typeof message === 'string' ? message : "Something went wrong!";
                toast.error(errorMessage);
            })
    };

    const getStatus = () => {
        getStatusAPI()
            .then((res) => {
                const list = res?.data?.data?.statuses || [];
                const formatted = list.map((item) => ({
                    label: (item?.name || "").trim(),
                    value: item?.id,
                    color: item?.color,
                }));
                setStatuses(formatted);
            })
            .catch((err) => {
                let message = err?.response?.data?.message;

                if (typeof message === 'object' && message !== null) {
                    if (message.error && message.error.errors && Array.isArray(message.error.errors)) {
                        const validationErrors = message.error.errors.map(err => err.msg).join(', ');
                        message = `Validation errors: ${validationErrors}`;
                    } else if (message.message) {
                        message = message.message;
                    } else if (message.error) {
                        message = message.error;
                    } else {
                        message = JSON.stringify(message);
                    }
                }

                if (err?.response?.status === 401) {
                    localStorage.removeItem('hoast_token')
                    localStorage.removeItem('hoast_refresh_token')
                    navigate('/login');
                }
                const errorMessage = typeof message === 'string' ? message : "Something went wrong!";
                toast.error(errorMessage);
            })
    };

    useEffect(() => {
        getMediums();
        getStatus();
    }, [])

    const convertCoordinatesToAddress = (lat, lng, shouldSetLocation = false) => {
        if (window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const formattedAddress = results[0].formatted_address;
                    const addressComponents = results[0].address_components;
                    let streetNumber = '';
                    let streetName = '';
                    let aptSuite = '';
                    let city = '';
                    let state = '';
                    let zipCode = '';

                    addressComponents.forEach(component => {
                        const types = component.types;
                        if (types.includes('street_number')) {
                            streetNumber = component.long_name;
                        }
                        if (types.includes('route')) {
                            streetName = component.long_name;
                        }
                        if (types.includes('subpremise') || types.includes('premise')) {
                            aptSuite = component.long_name;
                        }
                        if (types.includes('locality') || types.includes('postal_town') || types.includes('sublocality')) {
                            city = component.long_name;
                        }
                        if (types.includes('administrative_area_level_1')) {
                            state = component.long_name;
                        }
                        if (types.includes('postal_code')) {
                            zipCode = component.long_name;
                        }
                    });

                    const structuredAddress = {
                        streetAddress: streetNumber && streetName ? `${streetNumber} ${streetName}`.trim() : (streetName || formattedAddress),
                        aptSuite: aptSuite || '',
                        city: city || 'Surat',
                        state: state || 'Gujarat',
                        zipCode: zipCode || '395001'
                    };

                    setParsedAddressForModal(structuredAddress);

                    if (shouldSetLocation) {
                        setSelectedLocation({
                            lat: lat,
                            lng: lng,
                            ...structuredAddress
                        });
                    }
                }
            });
        }
    };

    useEffect(() => {
        if (isMainMapLoaded) {
            convertCoordinatesToAddress(mapCenter.lat, mapCenter.lng, false);
        }
    }, [isMainMapLoaded, mapCenter]);

    const parseAddressToComponents = (fullAddress) => {
        if (!fullAddress || !fullAddress.trim()) return null;

        if (window.google) {
            return new Promise((resolve) => {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ address: fullAddress }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        const addressComponents = results[0].address_components;
                        let streetNumber = '';
                        let streetName = '';
                        let aptSuite = '';
                        let city = '';
                        let state = '';
                        let zipCode = '';

                        addressComponents.forEach(component => {
                            const types = component.types;
                            if (types.includes('street_number')) {
                                streetNumber = component.long_name;
                            }
                            if (types.includes('route')) {
                                streetName = component.long_name;
                            }
                            if (types.includes('subpremise') || types.includes('premise')) {
                                aptSuite = component.long_name;
                            }
                            if (types.includes('locality') || types.includes('postal_town') || types.includes('sublocality')) {
                                city = component.long_name;
                            }
                            if (types.includes('administrative_area_level_1')) {
                                state = component.long_name;
                            }
                            if (types.includes('postal_code')) {
                                zipCode = component.long_name;
                            }
                        });

                        if (!streetName && !city) {
                            const parts = fullAddress.split(',').map(part => part.trim());
                            if (parts.length >= 3) {
                                streetName = parts[0] || '';
                                city = parts[1] || '';
                                state = parts[2] || '';
                                zipCode = parts[3] || '';
                            }
                        }

                        const parsedAddress = {
                            streetAddress: streetNumber && streetName ? `${streetNumber} ${streetName}`.trim() : (streetName || fullAddress),
                            aptSuite: aptSuite || '',
                            city: city || 'Surat',
                            state: state || 'Gujarat',
                            zipCode: zipCode || '395001'
                        };

                        resolve(parsedAddress);
                    } else {
                        const parts = fullAddress.split(',').map(part => part.trim());
                        if (parts.length >= 3) {
                            resolve({
                                streetAddress: parts[0] || '',
                                aptSuite: '',
                                city: parts[1] || 'Surat',
                                state: parts[2] || 'Gujarat',
                                zipCode: parts[3] || '395001'
                            });
                        } else {
                            resolve({
                                streetAddress: fullAddress,
                                aptSuite: '',
                                city: 'Surat',
                                state: 'Gujarat',
                                zipCode: '395001'
                            });
                        }
                    }
                });
            });
        }

        const parts = fullAddress.split(',').map(part => part.trim());
        if (parts.length >= 3) {
            return Promise.resolve({
                streetAddress: parts[0] || '',
                aptSuite: '',
                city: parts[1] || '',
                state: parts[2] || '',
                zipCode: parts[3] || ''
            });
        }

        return Promise.resolve({
            streetAddress: fullAddress,
            aptSuite: '',
            city: 'Surat',
            state: 'Gujarat',
            zipCode: '395001'
        });
    };

    const openLocationModal = async (currentFormLocation = '') => {
        const currentLocation = currentFormLocation || '';
        const plusCodeRegex = /[A-Z0-9]{4,}\+[A-Z0-9]{2,}(,\s)?/g;
        const finalAddress = currentLocation.replace(plusCodeRegex, '').trim();

        if (finalAddress && finalAddress.trim()) {
            try {
                const parsedAddress = await parseAddressToComponents(finalAddress);

                if (parsedAddress && Object.values(parsedAddress).some(val => val && val.trim())) {
                    setParsedAddressForModal(parsedAddress);
                    setIsLocationModalOpen(true);
                    return;
                }
            } catch (error) {
                console.error('Error parsing address:', error);
            }
        }

        setParsedAddressForModal(null);
        setIsLocationModalOpen(true);
    };

    const closeLocationModal = () => {
        setIsLocationModalOpen(false);
    };

    const handleStep1Submit = (values, { setSubmitting }) => {
        setStep1Data(values);
        setCurrentStep(2);
        setSubmitting(false);
    };

    const handleStep2Submit = (values, { setSubmitting }) => {
        // if (!selectedLocation?.city || selectedLocation.city.trim() === '') {
        //     toast.error('Please select a location with city details');
        //     setSubmitting(false);
        //     return;
        // }

        // if (!selectedLocation?.state || selectedLocation.state.trim() === '') {
        //     toast.error('Please select a location with state details');
        //     setSubmitting(false);
        //     return;
        // }

        setStep2Data(values);
        setCurrentStep(3);
        setSubmitting(false);
    };

    const handleStep3Submit = (values, { setSubmitting }) => {

        if (!step1Data.artTypes || step1Data.artTypes.length === 0) {
            toast.error('Please select at least one art type');
            setSubmitting(false);
            return;
        }

        if (!step1Data.bio || step1Data.bio.trim() === '') {
            toast.error('Please provide a bio');
            setSubmitting(false);
            return;
        }

        if (!step2Data.studioName || step2Data.studioName.trim() === '') {
            toast.error('Please provide a studio name');
            setSubmitting(false);
            return;
        }

        if (!step2Data.status) {
            toast.error('Please select a status');
            setSubmitting(false);
            return;
        }

        if (!step2Data.location || step2Data.location.trim() === '') {
            toast.error('Please provide a location');
            setSubmitting(false);
            return;
        }

        if (!step2Data.bannerNumber || step2Data.bannerNumber.trim() === '') {
            toast.error('Please provide a buzzer number');
            setSubmitting(false);
            return;
        }

        if (!values.artistStatement || values.artistStatement.trim() === '') {
            toast.error('Please provide an artist statement');
            setSubmitting(false);
            return;
        }

        const formData = new FormData();

        formData.append('medium_ids', step1Data.artTypes.join(','));
        if (profileImage) {
            formData.append('profile_image', profileImage);
        }
        formData.append('bio', step1Data.bio);
        formData.append('studio_name', step2Data.studioName);
        formData.append('status_id', step2Data.status);
        formData.append('studio_street', step2Data.location);
        formData.append('studio_landmark', parsedAddressForModal?.streetAddress || '');
        formData.append('studio_city', parsedAddressForModal?.city || '');
        formData.append('studio_zipcode', parsedAddressForModal?.zipCode || '');
        formData.append('studio_state', parsedAddressForModal?.state || '');
        formData.append('studio_buzzer_number', step2Data.bannerNumber);
        formData.append('studio_mission', values.artistStatement);

        const transformedSocialLinks = values.socialLinks
            .filter(link => link.url.trim())
            .map(link => ({
                studio_platform: link.platform,
                studio_platform_url: link.url
            }));

        formData.append('studio_social_links', JSON.stringify(transformedSocialLinks));

        if (mapCenter?.lat) {
            formData.append('latitude', mapCenter.lat);
        }
        if (mapCenter?.lng) {
            formData.append('longitude', mapCenter.lng);
        }
        formData.append('user_id', user_id);

        accountSetupAPI(formData)
            .then((res) => {
                if (res?.data?.status === 1) {
                    toast.success(res?.data?.message);
                    navigate("/profile");
                    localStorage.setItem("hoast_token", res?.data?.data?.access_token);
                    localStorage.setItem("hoast_refresh_token", res?.data?.data?.refresh_token);
                    if (res?.data?.data?.is_plan_active === true) {
                        localStorage.setItem("plan", "yes");
                    } else {
                        localStorage.setItem("plan", "no");
                    }
                    setSubmitting(false);
                } else {
                    toast.error(res?.data?.message);
                }
                setSubmitting(false);
            })
            .catch((err) => {
                let message = err?.response?.data?.message;

                if (typeof message === 'object' && message !== null) {
                    if (message.error && message.error.errors && Array.isArray(message.error.errors)) {
                        const validationErrors = message.error.errors.map(err => err.msg).join(', ');
                        message = `Validation errors: ${validationErrors}`;
                    } else if (message.message) {
                        message = message.message;
                    } else if (message.error) {
                        message = message.error;
                    } else {
                        message = JSON.stringify(message);
                    }
                }

                if (err?.response?.status === 401) {
                    localStorage.removeItem('hoast_token');
                    localStorage.removeItem('hoast_refresh_token');
                    navigate('/login');
                }
                const errorMessage = typeof message === 'string' ? message : "Account setup failed! Please try again.";
                toast.error(errorMessage);
                setSubmitting(false);
            });
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 5 * 1024 * 1024) {
                alert('File size must be less than 5MB');
                return;
            }
            setTempImageFile(file);
            setShowCropModal(true);
        }
    };

    const removeImage = () => {
        setProfileImage(null);
    };

    const handleCropComplete = (croppedImageUrl, croppedImageBlob) => {
        const croppedFile = new File([croppedImageBlob], 'profile-image.jpg', { type: 'image/jpeg' });
        setProfileImage(croppedFile);
        setShowCropModal(false);
    };

    const handleCropCancel = () => {
        setShowCropModal(false);
        setTempImageFile(null);
    };

    const goBack = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
        } else if (currentStep === 3) {
            setCurrentStep(2);
        }
    };

    const addSocialLink = (setFieldValue, values) => {
        const newLink = { platform: 'custom', url: '', icon: FiLink };
        const currentLinks = values.socialLinks || [];
        setFieldValue('socialLinks', [newLink, ...currentLinks]);
    };

    const removeSocialLink = (setFieldValue, values, index) => {
        const updatedSocialLinks = values.socialLinks.filter((_, i) => i !== index);
        setFieldValue('socialLinks', updatedSocialLinks);
    };

    const getSocialIcon = (platform) => {
        switch (platform) {
            case 'instagram': return FaInstagram;
            case 'facebook': return FaFacebook;
            case 'X': return FaXTwitter;
            case 'snapchat': return FaSnapchat;
            default: return FiLink;
        }
    };

    const renderStep1 = () => (
        <Formik
            initialValues={step1Data}
            validationSchema={profileValidationSchema}
            onSubmit={handleStep1Submit}
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={true}
            validateOnSubmit={true}
            enableReinitialize={true}
        >
            {({ values, setFieldValue, errors, touched, handleChange, handleBlur, submitCount, isSubmitting }) => (
                <Form>
                    <p className='mt-5 md:text-lg text-base'>Set Up Your Profile</p>
                    <CustomSelectOption
                        label="Medium"
                        name="artTypes"
                        value={values.artTypes}
                        onChange={(e) => setFieldValue("artTypes", e.target.value)}
                        onBlur={handleBlur}
                        error={errors.artTypes}
                        touched={touched.artTypes}
                        options={mediums}
                    />

                    <div className="grid grid-cols-12 md:pb-6 py-2 items-start gap-4">
                        <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                            Profile Picture
                        </label>
                        <div className="col-span-12 w-fit relative sm:col-span-8">
                            <div className="relative w-24 h-24 rounded-full bg-[#FAFAFA] border shadow flex items-center justify-center overflow-hidden">
                                {profileImage ? (
                                    <img
                                        src={URL.createObjectURL(profileImage)}
                                        alt="Preview"
                                        className="object-cover w-full h-full"
                                    />
                                ) : (
                                    <HiUser className="text-4xl text-gray-400" />
                                )}

                                <input
                                    type="file"
                                    accept="image/png, image/jpg, image/jpeg"
                                    onChange={handleImageChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                            </div>

                            {profileImage && (
                                <button
                                    type="button"
                                    onClick={removeImage}
                                    className="absolute bg-gray-100 shadow-xl rounded-lg top-0 right-0  p-1 text-red-500"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-12 text-sm items-start gap-4">
                        <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                            <p>Short Bio</p>
                        </label>
                        <div className="col-span-12 sm:col-span-8">
                            <div className="relative">
                                <textarea
                                    name="bio"
                                    placeholder="Enter Bio"
                                    value={values.bio}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className={`w-full border px-3 py-2 rounded-lg h-28 resize-none focus:outline-none ${(touched.bio || submitCount > 0) && errors.bio
                                        ? 'border-red-500'
                                        : 'border-gray-300'
                                        }`}
                                ></textarea>
                            </div>
                            <div className="flex justify-between items-center mt-1">
                                <div>
                                    {(touched.bio || submitCount > 0) && errors.bio && (
                                        <div className="text-red-500 text-xs">{errors.bio}</div>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {values.bio?.length || 0}/500
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end mt-10">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`md:w-fit w-full px-10 h-[52px] rounded-xl font-medium  ${isSubmitting ? 'opacity-50 cursor-not-allowed' : values.artTypes?.length > 0 && values.bio?.trim() ? ' text-white bg-[#212121]' : ' text-black bg-[#F0F1F3] cursor-not-allowed'}`}
                        >
                            {isSubmitting ? (
                                <EllipsisLoader color="red" />
                            ) : (
                                'Continue'
                            )}
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );

    useEffect(() => {
        if (currentStep === 2 && setFieldValueRef.current) {
            Object.keys(step2Data).forEach(key => {
                if (step2Data[key] && step2Data[key] !== '') {
                    setFieldValueRef.current(key, step2Data[key]);
                }
            });
        }
    }, [currentStep, step2Data]);

    const renderStep2 = () => (
        <Formik
            key={`step2-${currentStep}`}
            initialValues={{
                ...step2Data,
                location: step2Data.location || initialLocationStructuredAddress || ''
            }}
            validationSchema={studioValidationSchema}
            onSubmit={handleStep2Submit}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            validateOnSubmit={true}
            enableReinitialize={true}
        >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, submitCount }) => {
                setFieldValueRef.current = setFieldValue;

                const handleFieldChange = (e) => {
                    handleChange(e);
                    const { name, value } = e.target;
                    setStep2Data(prevData => ({
                        ...prevData,
                        [name]: value
                    }));
                };

                const handleStatusChange = (name, value) => {
                    setFieldValue(name, value);
                    setStep2Data(prevData => ({
                        ...prevData,
                        [name]: value
                    }));
                };

                return (
                    <Form onSubmit={handleSubmit}>
                        <p className='mt-5 md:text-lg text-base'>Studio Setup</p>
                        <div className="space-y-6 pt-5">
                            <div className="grid grid-cols-12 text-sm items-start gap-4">
                                <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                    Studio Name
                                </label>
                                <div className="col-span-12 sm:col-span-8">
                                    <input
                                        type="text"
                                        name="studioName"
                                        placeholder="Enter studio name"
                                        value={values.studioName}
                                        onChange={handleFieldChange}
                                        onBlur={handleBlur}
                                        className={`w-full border p-3 rounded-lg focus:outline-none ${submitCount > 0 && errors.studioName
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                    />
                                    {submitCount > 0 && errors.studioName && (
                                        <div className="text-red-500 text-xs mt-1">{errors.studioName}</div>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-12 text-sm items-start gap-4">
                                <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                    Status
                                </label>
                                <div className="col-span-12 sm:col-span-8">
                                    <CustomSelect
                                        name="status"
                                        value={values.status}
                                        onChange={handleStatusChange}
                                        options={statuses}
                                        error={errors.status}
                                        touched={submitCount > 0}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-12 text-sm items-start gap-4">
                                <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                    Location
                                </label>
                                <div className="col-span-12 sm:col-span-8">
                                    <div className="relative">
                                        {!isMainMapLoaded && (
                                            <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center z-10">
                                                <div className="text-center">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-800 mx-auto mb-1"></div>
                                                    <div className="text-gray-600 text-sm">Loading map...</div>
                                                </div>
                                            </div>
                                        )}

                                        {isMainMapLoaded && (
                                            <GoogleMap
                                                mapContainerStyle={containerStyle}
                                                center={mapCenter}
                                                zoom={15}
                                                options={{
                                                    zoomControl: false,
                                                    streetViewControl: false,
                                                    mapTypeControl: false,
                                                    fullscreenControl: false,
                                                }}
                                            >
                                                {selectedLocation && (
                                                    <Marker position={selectedLocation} />
                                                )}
                                            </GoogleMap>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between my-2">
                                        <button
                                            type="button"
                                            onClick={() => openLocationModal(values.location)}
                                            className="text-start flex items-center space-x-2 text-[#24B565] transition-colors"
                                        >
                                            <FiMapPin className="text-sm" />
                                            <span>Select Location</span>
                                        </button>
                                    </div>

                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="location"
                                            placeholder="Your location"
                                            value={values.location}
                                            className={`w-full border p-3 rounded-lg focus:outline-none ${submitCount > 0 && errors.location
                                                ? 'border-red-500'
                                                : 'border-gray-300 focus:border-gray-400'
                                                }`}
                                            disabled
                                        />
                                    </div>
                                    {submitCount > 0 && errors.location && (
                                        <div className="text-red-500 text-xs mt-1">{errors.location}</div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-12 text-sm items-start gap-4">
                                <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                    Buzzer Number
                                </label>
                                <div className="col-span-12 sm:col-span-8">
                                    <input
                                        type="text"
                                        name="bannerNumber"
                                        placeholder="Enter buzzer number"
                                        value={values.bannerNumber}
                                        onChange={handleFieldChange}
                                        onBlur={handleBlur}
                                        className={`w-full border p-3 rounded-lg focus:outline-none ${submitCount > 0 && errors.bannerNumber
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                    />
                                    {submitCount > 0 && errors.bannerNumber && (
                                        <div className="text-red-500 text-xs mt-1">{errors.bannerNumber}</div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-between items-center mt-10">
                                <button
                                    type="button"
                                    onClick={goBack}
                                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    <FiArrowLeft className="text-sm" />
                                    <span>Go back</span>
                                </button>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-fit px-10 h-[52px] rounded-xl font-medium  ${isSubmitting ? 'opacity-50 cursor-not-allowed' : values.studioName?.trim() && values.location?.trim() && values.bannerNumber?.trim() && values.status ? ' text-white bg-[#212121]' : ' text-black bg-[#F0F1F3] cursor-not-allowed'}`}
                                >
                                    {isSubmitting ? (
                                        <EllipsisLoader color="red" />
                                    ) : (
                                        'Continue'
                                    )}
                                </button>

                            </div>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );

    const renderStep3 = () => (
        <Formik
            initialValues={socialsInitialValues}
            validationSchema={socialsValidationSchema}
            onSubmit={handleStep3Submit}
            validateOnBlur={true}
            validateOnChange={true}
            validateOnMount={false}
            enableReinitialize={true}
            validateOnSubmit={true}
        >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, submitCount }) => (
                <Form onSubmit={handleSubmit}>
                    <p className='mt-5 md:text-lg text-base'>About Studio</p>
                    <div className="space-y-6 px-2 pt-5">
                        <div className="grid grid-cols-12 text-sm items-start gap-4">
                            <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                Artist's statement/Gallery's mission
                            </label>
                            <div className="col-span-12 sm:col-span-8">
                                <div className="relative">
                                    <textarea
                                        name="artistStatement"
                                        placeholder="Enter your artist statement or gallery mission"
                                        value={values.artistStatement}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        rows={6}
                                        className={`w-full border px-3 py-2 rounded-lg resize-none focus:outline-none ${(touched.artistStatement || submitCount > 0) && errors.artistStatement
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                    />
                                </div>
                                <div className="flex justify-between items-center mt-1">
                                    <div>
                                        {(touched.artistStatement || submitCount > 0) && errors.artistStatement && (
                                            <div className="text-red-500 text-xs">{errors.artistStatement}</div>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {values.artistStatement?.length || 0}/1000
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-12 text-sm items-start gap-4">
                            <label className="col-span-12 sm:col-span-4 text-sm font-medium text-gray-700 mt-2">
                                Socials
                            </label>
                            <div className="col-span-12 sm:col-span-8 space-y-3">
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => addSocialLink(setFieldValue, values)}
                                        className="flex items-center space-x-2 text-green-600 hover:text-green-700 transition-colors"
                                    >
                                        <FiPlus className="text-sm" />
                                        <span>Add New</span>
                                    </button>
                                </div>

                                {(values.socialLinks || []).map((link, index) => (
                                    <Field key={`social-${index}`} name={`socialLinks[${index}].url`}>
                                        {({ field, meta }) => (
                                            <div className="mb-3">
                                                <div className="flex gap-2 items-center relative">
                                                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                                        {React.createElement(getSocialIcon(link.platform), {
                                                            className: "text-gray-500 text-lg"
                                                        })}
                                                    </div>
                                                    <input
                                                        {...field}
                                                        type="text"
                                                        placeholder={`https://${link.platform}.com`}
                                                        className={`w-full pl-10 pr-3 py-3 rounded-lg border focus:outline-none ${meta.touched && meta.error
                                                            ? 'border-red-500'
                                                            : 'border-gray-300 focus:border-gray-400'
                                                            }`}
                                                    />
                                                    {link.platform === 'custom' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeSocialLink(setFieldValue, values, index)}
                                                            className="p-3 -mt-1 border text-red-500 rounded-lg shadow hover:text-red-700 transition-colors"
                                                            aria-label="Remove social link"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                                {meta.touched && meta.error && (
                                                    <div className="text-red-500 text-xs mt-1">{meta.error}</div>
                                                )}
                                            </div>
                                        )}
                                    </Field>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between items-center mt-10">
                            <button
                                type="button"
                                onClick={goBack}
                                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                <FiArrowLeft className="text-sm" />
                                <span>Go back</span>
                            </button>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-fit px-10 h-[52px] rounded-xl font-medium  ${isSubmitting || values.artistStatement?.trim() ? ' text-white bg-[#212121]' : ' text-black bg-[#F0F1F3] cursor-not-allowed'}`}
                            >
                                {isSubmitting ? (
                                    <EllipsisLoader color="white" />
                                ) : (
                                    'Finish Setup'
                                )}
                            </button>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );

    return (
        <div className="h-screen flex flex-col items-center justify-center px-4">
            <div className="bg-white md:p-5 p-3 rounded-xl w-full max-w-4xl">
                <div className='border-b'>
                    <h1 className="md:text-2xl text-xl font-semibold text-center mb-6">
                        Set Up Your Profile
                    </h1>

                    <div className="flex flex-col sm:flex-row sm:justify-center sm:space-x-8 mb-10 space-y-4 sm:space-y-0 text-sm font-medium">
                        <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-green-600' : 'bg-[#667085]'}`}>
                                <FiCheck className="text-white text-xs" />
                            </div>
                            <span>Set Up Your Profile</span>
                        </div>

                        <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-green-600' : 'bg-[#667085]'}`}>
                                <FiCheck className="text-white text-xs" />
                            </div>
                            <span>Studio Setup</span>
                        </div>

                        <div className={`flex items-center space-x-2 ${currentStep >= 3 ? 'text-green-600' : 'text-gray-400'}`}>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-green-600' : 'bg-[#667085]'}`}>
                                <FiCheck className="text-white text-xs" />
                            </div>
                            <span>About Studio</span>
                        </div>
                    </div>
                </div>

                <div className={` overflow-y-auto custom-scrollbar px-2 ${currentStep === 3 ? 'h-[60vh]' : 'md:h-fit h-[50vh]'}`}>
                    {currentStep === 1 ? renderStep1() : currentStep === 2 ? renderStep2() : renderStep3()}
                </div>
            </div>

            <LocationModal
                isOpen={isLocationModalOpen}
                onClose={closeLocationModal}
                onLocationSelect={(fullAddress, structuredAddress) => {
                    if (setFieldValueRef.current) {
                        setFieldValueRef.current('location', fullAddress);
                    }
                    setInitialLocationStructuredAddress(fullAddress);

                    setStep2Data(prevData => ({
                        ...prevData,
                        location: fullAddress
                    }));

                    closeLocationModal();
                }}
                canterlocatlon={(location) => {
                    setMapCenter({
                        lat: location.lat,
                        lng: location.lng
                    });

                    setSelectedLocation({
                        lat: location.lat,
                        lng: location.lng,
                        city: location.city || '',
                        state: location.state || '',
                        streetAddress: location.streetAddress || '',
                        aptSuite: location.aptSuite || '',
                        zipCode: location.zipCode || ''
                    });
                }}
                initialAddress={parsedAddressForModal}
            />

            <ProfileImageCrop
                isOpen={showCropModal}
                onClose={handleCropCancel}
                imageSrc={tempImageFile ? URL.createObjectURL(tempImageFile) : ''}
                onCropComplete={handleCropComplete}
                className="profile-image-crop-modal"
            />
        </div>
    );
}
